/*
 * Mural size calculator + minimum-order enforcement.
 *
 * Works on the product page AND inside the dynamically-loaded quick-view modal.
 * Behaviour is driven entirely by event delegation + a MutationObserver, so it
 * does not depend on inline <script> tags (which don't execute when quick-view
 * injects its content via innerHTML).
 *
 * Markup contract (rendered by snippets/tw-calculate-size-cost.liquid):
 *   <mural-calculator data-qty-target="Quantity-<section>" data-min-sqm="3">
 *     #wall-height (input[number])
 *     #wall-width  (input[number])
 *     #answer      (span)
 *     .order__danger (hidden message)
 *   </mural-calculator>
 */
(function () {
  'use strict';

  function getMin(calc) {
    return parseInt(calc.getAttribute('data-min-sqm'), 10) || 3;
  }

  // The container the calculator + its price/quantity live in. Used to scope
  // queries so a quick-view (or one product) never touches another's markup.
  function getScope(calc) {
    return calc.closest('.quick-view-product') || calc.closest('.shopify-section') || document;
  }

  function findQty(calc) {
    var targetId = calc.getAttribute('data-qty-target');
    if (targetId) {
      var byId = document.getElementById(targetId);
      if (byId) return byId;
    }
    var scope = getScope(calc);
    return (
      scope.querySelector('.product-form__quantity .quantity__input') ||
      scope.querySelector('[name="quantity"]')
    );
  }

  // Sets the cart quantity and notifies the theme's quantity-input component,
  // which re-validates the +/- buttons (disabling "minus" at the minimum).
  function setQuantity(qty, value) {
    if (!qty) return;
    qty.value = value;
    qty.dispatchEvent(new Event('input', { bubbles: true }));
    qty.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // Apply the minimum to the quantity input so Dawn's quantity-input clamps the
  // minus button (stepDown respects `min`, and validateQtyRules greys it out).
  function applyMin(calc) {
    var qty = findQty(calc);
    if (!qty) return;
    var min = getMin(calc);
    qty.min = min;
    qty.setAttribute('min', min);
    qty.dataset.min = min;
    // Trigger Dawn's validateQtyRules to refresh the minus button state.
    qty.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // Show the calculator's alert for 3s. Pass a message to override the default
  // ("Min order …"); omit it to restore the default text.
  function showMessage(calc, message) {
    var msg = calc.querySelector('.order__danger');
    if (!msg) return;
    if (calc._muralDefaultMsg == null) calc._muralDefaultMsg = msg.textContent;
    msg.textContent = message || calc._muralDefaultMsg;
    msg.hidden = false;
    clearTimeout(calc._muralTimer);
    calc._muralTimer = setTimeout(function () {
      msg.hidden = true;
    }, 3000);
  }

  function flash(calc) {
    showMessage(calc);
  }

  // Hide the alert immediately (e.g. once the customer fixes their input).
  function hideMessage(calc) {
    var msg = calc.querySelector('.order__danger');
    if (!msg) return;
    clearTimeout(calc._muralTimer);
    msg.hidden = true;
  }

  // Shown when a dimension field contains non-numeric input (e.g. "120e").
  var DIMENSION_HELP = 'Enter each wall dimension in numbers only — e.g. 120. Remove any letters or symbols.';

  // A number input reports badInput when the customer typed something that
  // can't be parsed as a number (letters, stray "e"/"+"/"-", etc.); in that
  // state input.value is "" so we'd otherwise treat it as simply empty.
  function hasBadInput(calc) {
    var h = calc.querySelector('#wall-height');
    var w = calc.querySelector('#wall-width');
    return (
      (h && h.validity && h.validity.badInput) ||
      (w && w.validity && w.validity.badInput)
    );
  }

  // The hidden line-item-property inputs (rendered by snippets/buy-buttons.liquid)
  // that are actually submitted with the order.
  function findProps(calc) {
    var scope = getScope(calc);
    return {
      height: scope.querySelector('#properties__height'),
      width: scope.querySelector('#properties__width')
    };
  }

  function calculate(calc) {
    var scope = getScope(calc);
    var heightInput = calc.querySelector('#wall-height');
    var widthInput = calc.querySelector('#wall-width');
    var answerSpan = calc.querySelector('#answer');
    var qty = findQty(calc);
    var min = getMin(calc);

    var propertiesHeight = scope.querySelector('#properties__height');
    var propertiesWidth = scope.querySelector('#properties__width');
    // Scope price items so we never rewrite other products' prices on the page.
    var priceItems = scope.querySelectorAll(
      '.price-item[data-saleprice], .price-item[data-regularprice]'
    );

    var wallWidth = parseFloat(widthInput && widthInput.value);
    var wallHeight = parseFloat(heightInput && heightInput.value);

    // (dimension + 5cm) / 100, area rounded UP to whole square metres.
    var area = Math.ceil(((wallWidth + 5) / 100) * ((wallHeight + 5) / 100));
    var invalid = isNaN(wallWidth) || isNaN(wallHeight) || area === 0;

    // Live feedback for non-numeric input (e.g. "120e"): show instructions,
    // and clear it the moment the entry is corrected.
    if (hasBadInput(calc)) {
      showMessage(calc, DIMENSION_HELP);
    } else {
      hideMessage(calc);
    }

    if (invalid) {
      if (qty) setQuantity(qty, 1);
      // Clear the submitted properties so a previously-valid entry can't linger.
      if (propertiesHeight) propertiesHeight.value = '';
      if (propertiesWidth) propertiesWidth.value = '';
      if (answerSpan) answerSpan.textContent = '1 Sqm';
    } else {
      if (qty) setQuantity(qty, area);
      if (propertiesHeight) propertiesHeight.value = wallHeight;
      if (propertiesWidth) propertiesWidth.value = wallWidth;
      if (answerSpan) answerSpan.textContent = area + ' Sqm';
    }

    // Live price update: unit price x sqm.
    Array.prototype.forEach.call(priceItems, function (priceItem) {
      var price = 0;
      var salePrice = priceItem.dataset.saleprice;
      var regularPrice = priceItem.dataset.regularprice;
      if (salePrice) {
        price = parseFloat(salePrice);
      } else if (regularPrice) {
        price = parseFloat(regularPrice);
      }

      var moneyElement = priceItem.querySelector('.money');
      if (!moneyElement) return;

      if (invalid) {
        moneyElement.innerText =
          '$' + price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      } else if (price > 0) {
        moneyElement.innerText =
          '$' + (price * area).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
    });
  }

  function init(calc) {
    if (!calc || calc.dataset.muralInit === '1') return;
    calc.dataset.muralInit = '1';
    applyMin(calc);
  }

  // Initialise any calculators already in the DOM (product page).
  function initAll(root) {
    (root || document).querySelectorAll('mural-calculator[data-min-sqm]').forEach(init);
  }

  // Recalculate as the customer types wall dimensions (delegated).
  document.addEventListener('input', function (event) {
    var target = event.target;
    if (!target || !target.matches || !target.matches('#wall-height, #wall-width')) return;
    var calc = target.closest('mural-calculator');
    if (calc) calculate(calc);
  });

  // Block add-to-cart below the minimum (capture phase so it runs before the
  // theme's own submit handler). Mirrors the old theme's behaviour + message.
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form) return;
      var calcs = document.querySelectorAll('mural-calculator[data-min-sqm]');
      for (var i = 0; i < calcs.length; i++) {
        var calc = calcs[i];
        var qty = findQty(calc);
        if (!qty) continue;
        var belongs = (qty.form && qty.form === form) || form.contains(qty);
        if (!belongs) continue;

        // Re-sync first: copies any entered dimensions into the hidden
        // properties (and clears them if the inputs are empty/invalid) so the
        // checks below reflect exactly what would be submitted.
        calculate(calc);

        var min = getMin(calc);
        var value = parseInt(qty.value, 10);
        var props = findProps(calc);
        var hasHeight = props.height && props.height.value.trim() !== '';
        var hasWidth = props.width && props.width.value.trim() !== '';

        // The dimensions are the real requirement — without them the order
        // ships with blank Wall Height / Wall Width. Block and prompt for them.
        if (hasBadInput(calc)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          showMessage(calc, DIMENSION_HELP);
        } else if (!hasHeight || !hasWidth) {
          event.preventDefault();
          event.stopImmediatePropagation();
          showMessage(calc, 'Please enter your wall dimensions');
        } else if (isNaN(value) || value < min) {
          event.preventDefault();
          event.stopImmediatePropagation();
          flash(calc);
        }
        return;
      }
    },
    true
  );

  // Watch for calculators added later (e.g. the quick-view modal content).
  if (typeof MutationObserver !== 'undefined') {
    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        Array.prototype.forEach.call(mutation.addedNodes, function (node) {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('mural-calculator[data-min-sqm]')) {
            init(node);
          } else if (node.querySelectorAll) {
            initAll(node);
          }
        });
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  // A variant change makes Dawn re-render the product form, which recreates the
  // hidden property inputs with blank values. Re-run calculate() so dimensions
  // still entered in the visible fields are copied back into the new inputs.
  // (constants.js + pubsub.js load before this file via defer, so these exist.)
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
    subscribe(PUB_SUB_EVENTS.variantChange, function () {
      // Defer so it runs after Dawn swaps in the new section markup.
      setTimeout(function () {
        document.querySelectorAll('mural-calculator[data-min-sqm]').forEach(calculate);
      }, 0);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
})();
